import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';

const card = 'bg-card text-card-foreground border border-border rounded-lg shadow-sm';
const field = 'border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-lg';
const primaryBtn = 'bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed';

export default function CitationTrackerOnboardingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const addingSite = searchParams.get('add') === '1';
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const [step, setStep] = useState('url');
  const [url, setUrl] = useState('');
  const [prompts, setPrompts] = useState([]);
  const [brandAliases, setBrandAliases] = useState([]);
  const [ownedHosts, setOwnedHosts] = useState([]);

  useEffect(() => {
    checkExistingTracker();
  }, []);

  const checkExistingTracker = async () => {
    try {
      const response = await fetch('/api/v1/citation-tracker/status', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      const data = await response.json();

      if (data.success && data.data.hasTracker && !addingSite) {
        navigate('/citation-tracker');
        return;
      }

      setLoading(false);
    } catch (err) {
      console.error('Failed to check tracker:', err);
      setLoading(false);
    }
  };

  const handleGeneratePrompts = async () => {
    if (!url) {
      setError('Please enter a URL');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const response = await fetch('/api/v1/citation-tracker/generate-prompts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ url })
      });

      const data = await response.json();

      if (data.success) {
        setPrompts(data.data.prompts);
        setBrandAliases(data.data.brandAliases);
        setOwnedHosts(data.data.ownedHosts);
        setStep('prompts');
      } else {
        setError(data.error || 'Failed to generate prompts');
      }
    } catch (err) {
      setError('Failed to generate prompts');
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdatePrompt = (index, value) => {
    const updated = [...prompts];
    updated[index] = value;
    setPrompts(updated);
  };

  const handleRemovePrompt = (index) => {
    setPrompts(prompts.filter((_, i) => i !== index));
  };

  const handleAddPrompt = () => {
    if (prompts.length < 10) {
      setPrompts([...prompts, '']);
    }
  };

  const handleCreateTracker = async () => {
    if (prompts.length === 0) {
      setError('Please add at least one prompt');
      return;
    }

    if (prompts.some(p => !p.trim())) {
      setError('Please fill in all prompts');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await fetch('/api/v1/citation-tracker/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          url,
          prompts,
          brandAliases,
          ownedHosts
        })
      });

      const data = await response.json();

      if (data.success) {
        if (data.data.status === 'active') {
          navigate('/citation-tracker');
          return;
        }

        const successUrl = `${window.location.origin}/citation-tracker?setup=complete`;
        const cancelUrl = `${window.location.origin}/citation-tracker/onboard?add=1`;

        const checkoutResponse = await fetch('/api/v1/subscription/create-checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            tier: 'citation_tracker',
            trackerId: data.data.id,
            successUrl,
            cancelUrl
          })
        });

        const checkoutData = await checkoutResponse.json();

        if (checkoutData.success) {
          window.location.href = checkoutData.data.url;
        } else {
          setError(checkoutData.error || 'Failed to start checkout');
        }
      } else {
        setError(data.error || 'Failed to create tracker');
      }
    } catch (err) {
      setError('Failed to create tracker');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">AI Citation Tracker Setup</h1>
      <p className="text-muted-foreground mb-8">Track your brand across ChatGPT, Perplexity, Gemini, and Google AI Overviews. Each site is $49/month.</p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-6 dark:bg-red-500/15 dark:border-red-400/40 dark:text-red-200">
          {error}
        </div>
      )}

      {step === 'url' && (
        <div className={`${card} p-6`}>
          <h2 className="text-xl font-semibold mb-4">Step 1: Enter Your URL</h2>
          <p className="text-muted-foreground mb-4">Which website do you want to track?</p>
          
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com"
            className={`w-full px-4 py-3 mb-4 ${field}`}
          />

          <button
            onClick={handleGeneratePrompts}
            disabled={generating || !url}
            className={`w-full py-3 ${primaryBtn}`}
          >
            {generating ? 'Generating Prompts...' : 'Continue'}
          </button>
        </div>
      )}

      {step === 'prompts' && (
        <div className={`${card} p-6`}>
          <h2 className="text-xl font-semibold mb-4">Step 2: Review & Edit Prompts</h2>
          <p className="text-muted-foreground mb-4">
            We suggested five queries from the homepage. Edit them, or add more, up to 10. They lock for the billing period once you confirm.
          </p>

          <div className="space-y-3 mb-6">
            {prompts.map((prompt, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={prompt}
                  onChange={(e) => handleUpdatePrompt(index, e.target.value)}
                  placeholder={`Prompt ${index + 1}`}
                  className={`flex-1 px-4 py-2 ${field}`}
                />
                {prompts.length > 1 && (
                  <button
                    onClick={() => handleRemovePrompt(index)}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg dark:text-red-300 dark:hover:bg-red-500/10"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>

          {prompts.length < 10 && (
            <button
              onClick={handleAddPrompt}
              className="w-full mb-6 px-4 py-2 border-2 border-dashed border-border text-muted-foreground rounded-lg hover:bg-accent"
            >
              + Add Prompt (max 10)
            </button>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => setStep('url')}
              className="flex-1 px-4 py-3 border border-border bg-card text-foreground rounded-lg font-semibold hover:bg-accent"
            >
              Back
            </button>
            <button
              onClick={handleCreateTracker}
              disabled={creating || prompts.length === 0}
              className={`flex-1 py-3 ${primaryBtn}`}
            >
              {creating ? 'Setting Up...' : 'Continue to Payment'}
            </button>
          </div>

          <p className="text-sm text-muted-foreground mt-4 text-center">
            Next: Subscribe for $49/mo to activate tracking
          </p>
        </div>
      )}
    </div>
  );
}
